import type { User } from '../types';

import { appealsApi, projectApi, userApi } from '../services/api';
import { logger } from '../services/logger';

interface UserFilterItem extends User {
  selected: boolean;
}

interface AppealView {
  id: string;
  unitId: string | null;
  projectId: string | null;
  assigneeId: string;
  leadId: string | null;
  name: string;
  phone: string;
  comment: string;
  status: string;
  createdAt: string;

  /**
   * A flag to indicate that this appeal was processed and will be removed from list
   * This will happen any activity on Lead was performed
   */
  processed: boolean;
  unitInfo: UnitInfo | null;
}

interface UnitInfo {
  project: {
    id: string;
    name: string;
  } | null;
  house: {
    id: string;
    name: string;
    sectionName: string;
  } | null;
  unit: {
    id: string;
    name: string;
  } | null;
}

export const APPEAL_STATUS = {
  NEW: '0',
  UNDERWAY: '1',
};

export class AppealsController {
  static $inject = ['$scope', '$translate'];

  loading = true;
  appeals: AppealView[] = [];
  users: UserFilterItem[] = [];
  selectedUsers: UserFilterItem[] = [];
  activeTabIndex: number = 0;
  sortDirection: 'asc' | 'desc' = 'desc';
  tabs: {
    title: string;
    appeals: AppealView[];
  }[] = [];

  constructor(
    private $scope: angular.IScope,
    private $translate: angular.translate.ITranslateService,
  ) {
    this.init();

    // if there was activity on lead that means
    // that some appeal should be removed from list
    $scope.$on('activity.update', () => this.init());
  }

  async init() {
    try {
      const [appeals, users] = await Promise.all([
        appealsApi.getAll(),
        userApi.getAll(),
      ]);

      this.users = users.map((user) => ({
        ...user,
        selected: !!user.current,
      }));

      const prevAppeals = new Map(this.appeals.map((item) => [item.id, item]));

      this.appeals = appeals.map((appeal): AppealView => {
        // it should be always string but valid data is not 100% guaranteed
        const assigneeId = appeal.last_action_by || String(appeal.created_by);
        prevAppeals.delete(appeal.id);

        return {
          id: appeal.id,
          unitId: appeal.unit_id,
          projectId: appeal.object_id,
          leadId: appeal.lead_id,
          assigneeId,
          name: appeal.name || this.$translate.instant('unit.popup.noname'),
          phone: appeal.phone,
          comment: appeal.custom_note || '',
          status: appeal.status,
          // will be filled in `this.fetchUnitInfo`
          unitInfo: null,
          processed: false,
          createdAt: appeal.created_at,
        };
      });

      prevAppeals.forEach((appeal) => {
        this.appeals.push({
          ...appeal,
          processed: true,
        });
      });

      this.fetchUnitInfo();

      this.$scope.$apply(() => {
        this.populateTabs();
      });
    } catch (error) {
      logger.error('Error loading appeals data', {
        error,
      });
    }
  }

  populateTabs() {
    const { $translate } = this;
    const newAppeals: AppealView[] = [];
    const underwayAppeals: AppealView[] = [];
    const selectedUsers = new Set<string>();
    let currentUser: UserFilterItem | null = null;

    for (const user of this.users) {
      if (user.selected) {
        selectedUsers.add(user.user_id);
      }

      if (user.current) {
        currentUser = user;
      }
    }

    if (!selectedUsers.size && currentUser) {
      // force at least current user to be selected
      currentUser.selected = true;
      selectedUsers.add(currentUser.user_id);
    }

    const appealsByUserId: { [key: string]: number } = {};

    for (const appeal of this.appeals) {
      if (!appealsByUserId[appeal.assigneeId]) {
        appealsByUserId[appeal.assigneeId] = 0;
      }

      appealsByUserId[appeal.assigneeId]++;

      if (!selectedUsers.has(String(appeal.assigneeId))) {
        continue;
      }

      (appeal.status === APPEAL_STATUS.NEW ? newAppeals : underwayAppeals).push(
        appeal,
      );
    }

    this.loading = false;
    this.tabs = [
      {
        title: $translate.instant('appeals.tabs.new.with.count', {
          count: newAppeals.length,
        }),
        appeals: newAppeals,
      },
      {
        title: $translate.instant('appeals.tabs.underway.with.count', {
          count: underwayAppeals.length,
        }),
        appeals: underwayAppeals,
      },
    ];

    // apply user counters
    this.users.forEach((user) => {
      user.username = user.username.replace(/ \(\d+\)$/, '');

      const counter = appealsByUserId[user.user_id];

      if (counter) {
        user.username += ` (${counter})`;
      }
    });

    this.tabs.forEach(({ appeals }) => {
      appeals.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();

        return this.sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    });

    if (this.activeTabIndex === 0 && !newAppeals.length) {
      this.activeTabIndex = 1;
    }

    if (this.activeTabIndex === 1 && !underwayAppeals.length) {
      this.activeTabIndex = 0;
    }
  }

  async fetchUnitInfo() {
    try {
      const jobs = {
        units: {} as {
          [key: string]: {
            promise: Promise<UnitInfo | null>;
            resolve(unitInfo: UnitInfo | null): void;
          };
        },
        projects: {} as {
          [key: string]: {
            promise: Promise<UnitInfo | null>;
            resolve(unitInfo: UnitInfo | null): void;
          };
        },
      };
      const createJob = () => {
        let resolve: (unitInfo: UnitInfo | null) => void = () => {};
        const promise = new Promise<UnitInfo | null>((cb) => {
          resolve = cb;
        });

        return {
          resolve,
          promise,
        };
      };

      // TODO: we can drop this logic in case when backend will start returning project/unit info
      const resolveUnitInfo = async (
        appeal: AppealView,
      ): Promise<UnitInfo | null> => {
        if (appeal.unitId) {
          if (!jobs.units[appeal.unitId]) {
            jobs.units[appeal.unitId] = createJob();
          }

          return jobs.units[appeal.unitId].promise;
        }

        if (appeal.projectId) {
          if (!jobs.projects[appeal.projectId]) {
            jobs.projects[appeal.projectId] = createJob();
          }

          return jobs.projects[appeal.projectId].promise;
        }

        return null;
      };

      await Promise.all(
        this.appeals
          .map(async (appeal) => {
            appeal.unitInfo = await resolveUnitInfo(appeal);
          })
          .concat(
            Promise.resolve().then(async () => {
              if (!Object.keys(jobs.projects).length) {
                return;
              }

              try {
                const projects = await projectApi.getAll();
                const projectsMap = new Map(
                  projects.map((project) => [project.id, project]),
                );

                Object.entries(jobs.projects).map(([projectId, job]) => {
                  const project = projectsMap.get(projectId);

                  if (!project) {
                    return;
                  }

                  job.resolve({
                    project: {
                      id: projectId,
                      name: project.name,
                    },
                    house: null,
                    unit: null,
                  });
                });
              } catch (error) {
                logger.error('Error populating projects', {
                  error,
                });
              }
            }),
          )
          .concat(
            Object.entries(jobs.units).map(async ([unitId, job]) => {
              try {
                // const unit = await unitApi.getById(unitId);

                // if (!unit) {
                //   return;
                // }

                job.resolve({
                  project: null,
                  house: null,
                  unit: {
                    id: unitId,
                    // {{::unitInfo.num_clean ? ('# ' + unitInfo.num_clean) : ('# ' + unitInfo.num)}}
                    name: `# ${unitId}`,
                  },
                });
              } catch (error) {
                logger.error(`Error loading unit ${unitId}`, {
                  error,
                });
              }
            }),
          ),
      );
    } catch (error) {
      logger.error('Error fetching unit info', {
        error,
      });
    }
  }

  toggleSort() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

    this.populateTabs();
  }
}
